import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { UserService } from '../services/user.service';

const USER_STORAGE_KEY = 'current_user';
const RETRY_HEADER = 'x-auth-retry';

const isAuthUrl = (url: string): boolean =>
  url.includes('/users/login') || url.includes('/users/register') || url.includes('/users/refresh');

const getStoredSessionToken = (): string | null => {
  try {
    if (typeof localStorage === 'undefined') {
      return null;
    }

    const stored = localStorage.getItem(USER_STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored) as { sessionToken?: string } | null;
    return parsed?.sessionToken ?? null;
  } catch {
    return null;
  }
};

@Injectable()
export class SessionTokenInterceptor implements HttpInterceptor {
  constructor(private readonly userService: UserService) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler) {
    const token = (() => {
      const sessionToken = this.userService.currentUserSnapshot?.sessionToken;
      if (sessionToken) {
        return sessionToken;
      }

      return getStoredSessionToken();
    })();

    const requestWithToken = token
      ? req.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`
          }
        })
      : req;

    return next.handle(requestWithToken).pipe(
      catchError((error: unknown) => {
        if (!(error instanceof HttpErrorResponse) || error.status !== 401 || isAuthUrl(req.url)) {
          return throwError(() => error);
        }

        if (req.headers.has(RETRY_HEADER)) {
          this.userService.clearSession();
          return throwError(() => error);
        }

        const refreshToken = this.userService.currentUserSnapshot?.refreshToken;
        if (!refreshToken) {
          this.userService.clearSession();
          return throwError(() => error);
        }

        return this.userService.refreshSessionTokens().pipe(
          switchMap(() => {
            const refreshedToken = this.userService.currentUserSnapshot?.sessionToken;
            if (!refreshedToken) {
              this.userService.clearSession();
              return throwError(() => error);
            }

            const retryReq = req.clone({
              setHeaders: {
                Authorization: `Bearer ${refreshedToken}`,
                [RETRY_HEADER]: '1'
              }
            });
            return next.handle(retryReq);
          }),
          catchError(() => {
            this.userService.clearSession();
            return throwError(() => error);
          })
        );
      })
    );
  }
}
